import {
  isKeycloakClientRole,
  type KeycloakClientRole,
} from '../../auth/clientRoles';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { LiksFormFieldLabel } from '../../components/liks/LiksFormFieldLabel/LiksFormFieldLabel';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../i18n/useI18n';

type CreateTenantUserDialogRequiredFieldsProps = {
  roleFieldId: string;
  sendInviteEmailFieldId: string;
  role: KeycloakClientRole;
  creatableRoles: readonly KeycloakClientRole[];
  onRoleChange: (role: KeycloakClientRole) => void;
  email: string;
  onEmailChange: (value: string) => void;
  isEmailInvalid: boolean;
  username: string;
  onUsernameChange: (value: string) => void;
  isUsernameInvalid: boolean;
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  sendInviteEmail: boolean;
  onSendInviteEmailChange: (checked: boolean) => void;
  isSubmitting: boolean;
};

export function CreateTenantUserDialogRequiredFields({
  roleFieldId,
  sendInviteEmailFieldId,
  role,
  creatableRoles,
  onRoleChange,
  email,
  onEmailChange,
  isEmailInvalid,
  username,
  onUsernameChange,
  isUsernameInvalid,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  sendInviteEmail,
  onSendInviteEmailChange,
  isSubmitting,
}: CreateTenantUserDialogRequiredFieldsProps) {
  const { translate } = useI18n();

  const roleOptions = creatableRoles.map((value) => ({
    value,
    label: translate(`organisationManagement.users.roles.${value}`),
  }));

  return (
    <>
      <div className="liks-text-form-field">
        <LiksFormFieldLabel htmlFor={roleFieldId} required>
          {translate('organisationManagement.users.roleLabel')}
        </LiksFormFieldLabel>
        <LiksDropdown
          id={roleFieldId}
          className="lac-create-user__dropdown"
          variant="on-light"
          value={role}
          onChange={(value) => {
            if (isKeycloakClientRole(value) && creatableRoles.includes(value)) {
              onRoleChange(value);
            }
          }}
          options={roleOptions}
          aria-label={translate('organisationManagement.users.roleLabel')}
          disabled={isSubmitting}
        />
      </div>

      <div className="lac-create-user__row">
        <LiksTextFormField
          label={translate('organisationManagement.users.create.emailLabel')}
          required
          type="email"
          value={email}
          onChange={onEmailChange}
          autoFocus
          disabled={isSubmitting}
          autoComplete="off"
          error={
            isEmailInvalid
              ? translate('organisationManagement.users.create.emailInvalid')
              : null
          }
        />

        <LiksTextFormField
          label={translate('organisationManagement.users.create.usernameLabel')}
          required
          value={username}
          onChange={onUsernameChange}
          disabled={isSubmitting}
          autoComplete="off"
          error={
            isUsernameInvalid
              ? translate(
                  'organisationManagement.users.create.usernameNoSpaces',
                )
              : null
          }
        />
      </div>

      <div className="lac-create-user__row">
        <LiksTextFormField
          label={translate(
            'organisationManagement.users.create.firstNameLabel',
          )}
          required
          value={firstName}
          onChange={onFirstNameChange}
          disabled={isSubmitting}
          autoComplete="off"
        />

        <LiksTextFormField
          label={translate('organisationManagement.users.create.lastNameLabel')}
          required
          value={lastName}
          onChange={onLastNameChange}
          disabled={isSubmitting}
          autoComplete="off"
        />
      </div>

      <label
        className="lac-create-user__checkbox"
        htmlFor={sendInviteEmailFieldId}
      >
        <input
          id={sendInviteEmailFieldId}
          type="checkbox"
          checked={sendInviteEmail}
          onChange={(event) => onSendInviteEmailChange(event.target.checked)}
          disabled={isSubmitting}
        />
        <span>
          {translate(
            'organisationManagement.users.create.sendInviteEmailLabel',
          )}
        </span>
      </label>
    </>
  );
}
